/**
 * Шапка доски — одна строка: проект, подразделение, доска, действия.
 * Слева — название проекта, вкладки подразделений и селект досок
 * (§8.5), справа — «Создать задачу», «Создать доску», архив и
 * шестерёнка настроек (§9.1).
 *
 * §8.5 требует показывать режим привязки: пользователь должен понимать,
 * откуда берётся проект у задачи — с доски или со столбца. Поэтому проект
 * стоит первым в строке, а не подписью под ней: отдельная строка внизу
 * добавляла шапке третий ряд и терялась.
 *
 * Название доски — выбранное значение селекта, а не отдельный заголовок:
 * этим же селектом доски и переключаются.
 *
 * На телефоне (docs/08-mobile-roadmap.md, M3) в строке действий остаётся
 * «Создать задачу», остальное — в меню «Ещё»: шесть кнопок в ряд были
 * шире экрана и растягивали страницу вбок.
 */

import { Permission, ProjectBindingMode, WatchTargetType } from "@taskflow/contracts";
import { ArchiveRestore, Eye } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PermissionGate } from "@/shared/auth/PermissionGate";
import { usePermission } from "@taskflow/client-core";
import { useIsPhone } from "@/shared/lib/media";
import { Button, IconButton, Menu, type MenuAction } from "@/shared/ui";
import { Archive, MoreHorizontal, Plus, Settings, Users } from "@/shared/ui/motionIcons";

import type { BoardDetail } from "@taskflow/client-core/boards";
import { BoardSwitcher } from "./BoardSwitcher";
import { CreateBoardDialog } from "./CreateBoardDialog";
import { useWatchMenu } from "../watching/useWatchMenu";

export function BoardHeader({
  board,
  onCreateTask,
}: {
  board: BoardDetail;
  /** Кнопка «Создать задачу» (§11.3); без обработчика — нет права, кнопки нет. */
  onCreateTask?: () => void;
}) {
  // В личную доску пользователей не добавить — участников у неё нет.
  const canMembers =
    usePermission(Permission.BOARD_MEMBERS, board.permissions) && !board.isPersonal;
  const canSettings = usePermission(
    [
      Permission.BOARD_SETTINGS,
      Permission.BOARD_MEMBERS,
      Permission.BOARD_COLUMNS,
      Permission.TAG_MANAGE,
    ],
    board.permissions,
  );
  const phone = useIsPhone();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const watchMenu = useWatchMenu();
  // Наблюдение за доской и её проектом. На личной доске — не за кем:
  // о собственных действиях не уведомляют, а других участников нет.
  const watchActions: MenuAction[] = board.isPersonal
    ? []
    : [
        ...watchMenu.actionsFor({
          targetType: WatchTargetType.BOARD,
          targetId: board.id,
          name: board.name,
        }),
        ...(board.projectBindingMode === ProjectBindingMode.BOARD &&
        board.projectId !== undefined &&
        board.projectId !== null &&
        board.projectName !== null
          ? watchMenu.actionsFor(
              {
                targetType: WatchTargetType.PROJECT,
                targetId: board.projectId,
                name: board.projectName,
              },
              true,
            )
          : []),
      ];

  return (
    <>
      <header className="flex shrink-0 flex-col border-b border-line bg-surface px-4 py-2.5">
        {/* Видимое название — активная вкладка доски; заголовок остаётся
            для скринридера и структуры страницы. */}
        <h1 className="sr-only">{board.name}</h1>

        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            <BoardSwitcher departmentCode={board.departmentCode} boardId={board.id} />
          </div>

          {phone ? (
            <div className="flex w-full items-center gap-2">
              {onCreateTask !== undefined && (
                <Button icon={Plus} onClick={onCreateTask} className="flex-1">
                  Создать задачу
                </Button>
              )}
              <Menu
                trigger={
                  <IconButton icon={MoreHorizontal} label="Ещё действия с доской" variant="secondary" />
                }
                actions={[
                  {
                    key: "archive",
                    label: "Архив задач",
                    icon: Archive,
                    onSelect: () => void navigate(`/boards/${board.id}/archive`),
                  },
                  {
                    key: "archived-boards",
                    label: "Архив досок",
                    icon: ArchiveRestore,
                    onSelect: () => void navigate("/boards/archived"),
                  },
                  ...(canMembers
                    ? [
                        {
                          key: "members",
                          label: "Участники доски",
                          icon: Users,
                          onSelect: () => void navigate(`/boards/${board.id}/settings?tab=members`),
                        } satisfies MenuAction,
                      ]
                    : []),
                  ...(canSettings
                    ? [
                        {
                          key: "settings",
                          label: "Настройки доски",
                          icon: Settings,
                          onSelect: () => void navigate(`/boards/${board.id}/settings`),
                        } satisfies MenuAction,
                      ]
                    : []),
                  ...watchActions.map((action, index) =>
                    index === 0 ? { ...action, separated: true } : action,
                  ),
                  // Личную доску заводит любой сотрудник — пункт есть у всех.
                  {
                    key: "create-board",
                    label: "Создать доску",
                    icon: Plus,
                    separated: true,
                    onSelect: () => setCreating(true),
                  },
                ]}
              />
            </div>
          ) : (
          <div className="flex shrink-0 items-center gap-1.5">
            {onCreateTask !== undefined && (
              <Button size="sm" icon={Plus} onClick={onCreateTask}>
                Создать задачу
              </Button>
            )}

            {/* Личную доску заводит любой сотрудник — кнопка есть у всех. */}
            <Button variant="secondary" size="sm" icon={Plus} onClick={() => setCreating(true)}>
              Создать доску
            </Button>

            {/* Архив — отдельный экран доски, а не последний столбец (§0.3).
                Подпись — из макета: рядом есть и архив досок, «Архив» без
                уточнения стал бы двусмысленным. */}
            <Link to={`/boards/${board.id}/archive`}>
              <Button variant="secondary" size="sm" icon={Archive}>
                Архив задач
              </Button>
            </Link>

            <Link to="/boards/archived">
              <IconButton icon={ArchiveRestore} label="Архив досок" variant="secondary" />
            </Link>

            {watchActions.length > 0 && (
              <Menu
                trigger={<IconButton icon={Eye} label="Наблюдение" variant="secondary" />}
                actions={watchActions}
              />
            )}

            {canMembers && (
              <Link to={`/boards/${board.id}/settings?tab=members`}>
                <IconButton icon={Users} label="Участники доски" variant="secondary" />
              </Link>
            )}

            {/* §9.1: настройки — единственная точка входа, через шестерёнку. */}
            <PermissionGate
              permission={[
                Permission.BOARD_SETTINGS,
                Permission.BOARD_MEMBERS,
                Permission.BOARD_COLUMNS,
                Permission.TAG_MANAGE,
              ]}
              boardPermissions={board.permissions}
            >
              <Link to={`/boards/${board.id}/settings`}>
                <IconButton icon={Settings} label="Настройки доски" variant="secondary" />
              </Link>
            </PermissionGate>
          </div>
          )}
        </div>
      </header>

      {watchMenu.dialog}

      {creating && (
        <CreateBoardDialog
          open
          onClose={() => setCreating(false)}
          initialDepartmentId={board.departmentId}
        />
      )}
    </>
  );
}
